/**
 * NotificationItem Component
 *
 * Renders a single notification with read/unread styling, hover actions,
 * and optional action navigation. Supports compact mode for dropdowns.
 */

import { useReimbursementLink } from '@/features/transactions/components/reimbursement-link/reimbursement-link-context';
import { Button, Icon, IconKey } from '@/shared/components';
import { Badge } from '@/shared/components/ui/badge';
import type { NotificationRead } from '@/shared/types';
import { cn } from '@/shared/utils';

import { useNavigate } from 'react-router-dom';
import { getSeverityStyles, getTypeLabel, timeAgo } from '../utils';

interface NotificationItemProps {
  notification: NotificationRead;
  compact?: boolean;
  onRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
}

export function NotificationItem({ notification, compact, onRead, onDismiss }: NotificationItemProps) {
  const navigate = useNavigate();
  const { openLinkModal } = useReimbursementLink();
  const styles = getSeverityStyles(notification.severity);
  const isUnread = !notification.is_read;

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.action_url) return;
    if (isUnread && onRead) onRead(notification.id);

    // Reimbursement requests open the link modal instead of navigating
    if (notification.notification_type === 'reimbursement_request' && notification.metadata) {
      const txnId = notification.metadata.transaction_id as string;
      if (txnId) {
        openLinkModal(txnId, notification.id, notification.metadata, notification.action_label);
        return;
      }
    }

    navigate(notification.action_url);
  };

  return (
    <div
      className={cn(
        'group relative px-4 py-3 border-l-2 transition-colors',
        isUnread ? [styles.border, styles.bg] : 'border-l-transparent opacity-60 bg-background'
      )}
    >
      {/* Title row */}
      <div className='flex items-start justify-between gap-2'>
        <div className='flex items-center gap-2 min-w-0 flex-1'>
          {isUnread && <span className={cn('h-2 w-2 rounded-full shrink-0', styles.dot)} />}
          <span className={cn('text-sm truncate text-text', isUnread ? 'font-semibold' : 'font-medium')}>{notification.title}</span>
          <Badge variant='outline' className='text-[10px] px-1.5 py-0'>
            {getTypeLabel(notification.notification_type)}
          </Badge>
        </div>
      </div>

      {/* Body */}
      <p className={cn('text-[11px] text-muted-foreground mt-0.5', compact ? 'line-clamp-1' : 'line-clamp-3', isUnread && 'pl-4')}>
        {notification.body}
      </p>

      {/* Footer (non-compact only) */}
      {!compact && (
        <div className='flex items-center gap-2'>
          {isUnread && <div className='w-4' />}

          {notification.action_url && notification.action_label && (
            <Button variant='link' size='sm' onClick={handleActionClick}>
              {notification.action_label} <Icon icon={IconKey.ArrowRight} size={11} />
            </Button>
          )}
          <span className='ml-auto text-[11px] text-muted-foreground whitespace-nowrap shrink-0'>{timeAgo(notification.created_at)}</span>
        </div>
      )}

      {/* Hover actions (non-compact only) */}
      {!compact && (
        <div className='absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
          {isUnread && onRead && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRead(notification.id);
              }}
              className='p-1 rounded-sm hover:bg-secondary/50 text-muted-foreground hover:text-text transition-colors'
              title='Mark as read'
            >
              <Icon icon={IconKey.Check} size={14} />
            </button>
          )}
          {onDismiss && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(notification.id);
              }}
              className='p-1 rounded-sm hover:bg-secondary/50 text-muted-foreground hover:text-text transition-colors'
              title='Dismiss'
            >
              <Icon icon={IconKey.X} size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
